import { useCallback, useMemo } from 'react';

import { DEFAULT_COOKTOP_QTECODEID, MATERIAL_CUTOUT_DEFAULTS, QTEType } from '@common/constants';
import { ApplianceModel, CutoutLaborModel } from '@common/models';
import { createFetchProductsHook } from '@core/utils/createFetchProductsHook';

import { useFetchCutoutLaborQuery } from '../cutoutLaborService';

export const useCutoutLaborProducts = createFetchProductsHook({
    apiQueryHook: useFetchCutoutLaborQuery,
});

/**
 * @param props.materialType material product line of the countertop
 * @param props.sink sink appliance model record
 * @param props._source used for logging/debugging
 */
export interface SelectSinkCutoutProps {
    materialType: string | undefined | null;
    sink: ApplianceModel;
    _source?: string;
}

export interface UseSelectedCutoutLaborResponse {
    cooktopCutout: CutoutLaborModel | undefined;
    selectSinkCutout: (props: SelectSinkCutoutProps) => CutoutLaborModel | undefined;
}

export const useSelectCutoutLabor = (): UseSelectedCutoutLaborResponse => {
    const { data: cutoutLaborProducts } = useCutoutLaborProducts();

    const cooktopCutout = useMemo(() => {
        const matchedCutouts = cutoutLaborProducts
            .filter((product) => {
                return product.qteCodeId === DEFAULT_COOKTOP_QTECODEID;
            })
            .sort((a, b) => b.price - a.price || b.productId - a.productId); // price high-low then id high-low
        return matchedCutouts.length > 0 ? matchedCutouts[0] : undefined;
    }, [cutoutLaborProducts]);

    const selectSinkCutout = useCallback(
        (props: SelectSinkCutoutProps) => {
            const { materialType, sink } = props;
            if (!sink) {
                return;
            }

            const matchedCutouts = cutoutLaborProducts
                .filter((product) => {
                    return sink.qteTypeId === QTEType.Sink && product.qteCodeId === sink.qteCodeId;
                })
                .sort((a, b) => b.price - a.price || b.productId - a.productId); // price high-low then id high-low

            if (matchedCutouts.length > 0) {
                return matchedCutouts[0];
            }

            return MATERIAL_CUTOUT_DEFAULTS.reduce((found: CutoutLaborModel | undefined, rule) => {
                if (found) {
                    return found;
                }

                // if the rule.materialType is null, then that's always one to test. if not, test if the materials match
                const doTestRule = !rule.materialType || !!(materialType ?? '').match(rule.materialType);

                return doTestRule
                    ? cutoutLaborProducts.find((product) => {
                          return product.qteCodeId === rule.cutoutQteCodeId;
                      })
                    : found;
            }, undefined);

            // console.log('==selectSinkCutout default', props, defaultCutout);
            // return defaultCutout;
        },
        [cutoutLaborProducts]
    );

    return {
        cooktopCutout,
        selectSinkCutout,
    };
};
